export const sanitizeUrl = (url: string): string => {
  return url.replace(/(\r\n|\n|\r)/gm, '')
}

// encode url does not work with some urls (cf https://welcoming-group.atlassian.net/browse/PWSF-694)
export const encodeSubUrls = (url: string): string => {
  const [baseUrl, queryParams] = url.split('?')
  if (!queryParams) return url
  const encodedParams = queryParams.replace(/(https?:\/\/[^\s&]+)/g, (subUrl) => encodeURIComponent(subUrl))
  return `${baseUrl}?${encodedParams}`
}

export const sanitizeAndEncodeUrl = (url: string): string => {
  const sanitizedUrl = sanitizeUrl(url)
  return encodeSubUrls(sanitizedUrl)
}

export const UTM_GENERATOR_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_term',
  'utm_content',
] as const

export type UtmGeneratorKey = (typeof UTM_GENERATOR_KEYS)[number]
export type UtmGeneratorValues = Record<UtmGeneratorKey, string>
export type QueryParamValues = Record<string, string>

const splitUrl = (url: string): { baseUrl: string; searchParams: URLSearchParams; hash: string } => {
  const [urlWithoutHash, hashFragment = ''] = url.split('#', 2)
  const [baseUrl, queryString = ''] = urlWithoutHash.split('?', 2)

  return {
    baseUrl,
    searchParams: new URLSearchParams(queryString),
    hash: hashFragment ? `#${hashFragment}` : '',
  }
}

const normalizeUtmKey = (key: string): UtmGeneratorKey | null => {
  const normalized = key.toLowerCase()
  return UTM_GENERATOR_KEYS.includes(normalized as UtmGeneratorKey) ? (normalized as UtmGeneratorKey) : null
}

export const getUsedUtmKeys = (url: string): UtmGeneratorKey[] => {
  const { searchParams } = splitUrl(url)
  const usedUtmKeys = new Set<UtmGeneratorKey>()

  searchParams.forEach((_value, key) => {
    const normalizedKey = normalizeUtmKey(key)
    if (normalizedKey) usedUtmKeys.add(normalizedKey)
  })

  return UTM_GENERATOR_KEYS.filter((key) => usedUtmKeys.has(key))
}

export const extractUtmValuesFromUrl = (url: string): Partial<UtmGeneratorValues> => {
  const { searchParams } = splitUrl(url)
  const extractedValues: Partial<UtmGeneratorValues> = {}

  searchParams.forEach((value, key) => {
    const normalizedKey = normalizeUtmKey(key)
    if (normalizedKey) extractedValues[normalizedKey] = value
  })

  return extractedValues
}

export const buildUtmGeneratorValuesFromUrl = (url: string, defaultValues: UtmGeneratorValues): UtmGeneratorValues => {
  return {
    ...defaultValues,
    ...extractUtmValuesFromUrl(url),
  }
}

export const mergeUtmValuesIntoUrl = (url: string, utmValues: Partial<UtmGeneratorValues>): string => {
  return mergeQueryParamsIntoUrl(
    url,
    Object.entries(utmValues).reduce<QueryParamValues>((acc, [key, value]) => {
      acc[key] = value ?? ''
      return acc
    }, {}),
    UTM_GENERATOR_KEYS
  )
}

export const extractQueryParamsFromUrl = (url: string): QueryParamValues => {
  const { searchParams } = splitUrl(url)
  const queryParams: QueryParamValues = {}

  searchParams.forEach((value, key) => {
    queryParams[key] = value
  })

  return queryParams
}

export const mergeQueryParamsIntoUrl = (
  url: string,
  queryValues: QueryParamValues,
  keysToDelete: ReadonlyArray<string> = []
): string => {
  const { baseUrl, searchParams, hash } = splitUrl(url)

  keysToDelete.forEach((key) => searchParams.delete(key))

  Object.entries(queryValues).forEach(([key, value]) => {
    const normalizedKey = key.trim()
    if (!normalizedKey) return
    if (value && value.trim().length > 0) searchParams.set(normalizedKey, value.trim())
    else searchParams.delete(normalizedKey)
  })

  const queryString = Array.from(searchParams.entries())
    .map(([key, value]) => `${key}=${value}`)
    .join('&')
  const urlWithQuery = queryString ? `${baseUrl}?${queryString}` : baseUrl
  return `${urlWithQuery}${hash}`
}
